import { DestroyRef, Injectable, inject, NgZone } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivityDetectorService } from './activity-detector.service';

@Injectable({
  providedIn: 'root',
})
export class KioskAutoReloadService {
  private readonly destroy$ = new Subject<void>();

  private readonly reloadIntervalMs = 60 * 60 * 1000; // 30 minutos
  private readonly idleWindowMs = 2 * 60 * 1000; // 2 minutos

  private readonly destroyRef = inject(DestroyRef);

  private readonly ngZone = inject(NgZone);

  private started = false;
  private reloadPending = false;
  private idleTimerId: ReturnType<typeof setTimeout> | null = null;
  private reloadWindowSubscription: Subscription | null = null;

  constructor(
    private readonly activityDetectorService: ActivityDetectorService,
  ) {
    this.destroyRef.onDestroy(() => this.destroy());
  }

  start(): void {
    if (this.started) return;

    this.started = true;

    this.activityDetectorService.activityDetected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.reloadPending) return;
        this.restartIdleCountdown();
      });

    this.ngZone.runOutsideAngular(() => {
      this.reloadWindowSubscription = timer(this.reloadIntervalMs, this.reloadIntervalMs)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.ngZone.run(() => this.openReloadWindow());
        });
    });
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearIdleTimer();
    this.reloadWindowSubscription?.unsubscribe();
    this.reloadWindowSubscription = null;
  }

  private openReloadWindow(): void {
    this.reloadPending = true;
    this.restartIdleCountdown();
  }

  private restartIdleCountdown(): void {
    this.clearIdleTimer();

    this.ngZone.runOutsideAngular(() => {
      this.idleTimerId = setTimeout(() => {
        this.forceReload();
      }, this.idleWindowMs);
    });
  }

  private clearIdleTimer(): void {
    if (this.idleTimerId !== null) {
      clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
  }

  private forceReload(): void {
    this.reloadPending = false;
    this.clearIdleTimer();
    globalThis.location.reload();
  }
}
